import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import {
    addKpiAccessRule, deleteKpiAccessRule, getKpiAccess, getKpiEmployees, getKpiLinks, setKpiCardEmployee, setKpiGroupUnits,
} from "../services/kpiApi";
import { childrenMap, matchEmployee, rootUnits } from "../utils/companyTree";
import { periodLabel } from "../utils/kpiFormat";

/**
 * KPI → Настройки. [Claude] 2026-11-03, Чат 6, №35.
 * «Привязка» — вручную: таблица КПЭ ↔ подразделения структуры, карта ↔ сотрудник (подсказка «Подобрать по ФИО»).
 * «Доступ» — отдельная настраиваемая история: кто смотрит / правит / управляет, ко всем таблицам или к одной.
 */

const input = "min-h-9 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-amber-400";
const btn = "min-h-9 rounded-lg px-3 py-1.5 text-sm font-semibold transition disabled:opacity-50";
const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;
const btnMain = `${btn} bg-[#e0bb48] text-black hover:bg-amber-400`;

function flatUnits(units) {
    const kids = childrenMap(units);
    const out = [];
    const walk = (u, depth) => { out.push({ ...u, depth }); (kids.get(u.id) || []).forEach((c) => walk(c, depth + 1)); };
    rootUnits(units).forEach((r) => walk(r, 0));
    return out;
}

function UnitPicker({ units, value, onSave }) {
    const [open, setOpen] = useState(false);
    const [sel, setSel] = useState(new Set(value));
    const [q, setQ] = useState("");
    const valueKey = value.join(",");
    useEffect(() => { setSel(new Set(valueKey ? valueKey.split(",").map(Number) : [])); }, [valueKey]);
    const byId = new Map(units.map((u) => [u.id, u]));
    const shown = units.filter((u) => !q.trim() || String(u.name).toLowerCase().includes(q.trim().toLowerCase()));
    return (
        <div>
            <div className="flex flex-wrap items-center gap-1">
                {value.length ? value.map((id) => (
                    <span key={id} className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-900">{byId.get(id)?.name || `#${id}`}</span>
                )) : <span className="text-xs text-slate-400">не привязана</span>}
                <button type="button" className="ml-1 text-xs text-slate-500 underline" onClick={() => setOpen((v) => !v)}>{open ? "закрыть" : "изменить"}</button>
            </div>
            {open && (
                <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2">
                    <input className={`${input} mb-2 w-full`} placeholder="Поиск подразделения" value={q} onChange={(e) => setQ(e.target.value)} />
                    <div className="max-h-64 overflow-y-auto">
                        {shown.map((u) => (
                            <label key={u.id} className="flex items-center gap-2 py-0.5 text-sm" style={{ paddingLeft: q ? 0 : u.depth * 14 }}>
                                <input type="checkbox" checked={sel.has(u.id)} onChange={(e) => setSel((s) => {
                                    const n = new Set(s); if (e.target.checked) n.add(u.id); else n.delete(u.id); return n;
                                })} />
                                {u.name}
                            </label>
                        ))}
                    </div>
                    <div className="mt-2 flex gap-2">
                        <button type="button" className={btnMain} onClick={async () => { if (await onSave([...sel])) setOpen(false); }}>Сохранить</button>
                        <button type="button" className={btnGhost} onClick={() => { setSel(new Set(value)); setOpen(false); }}>Отмена</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function LinksTab() {
    const { showAlert } = useAlertContext();
    const [data, setData] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [openGroup, setOpenGroup] = useState(null);

    const load = useCallback(async () => {
        try {
            const [l, e] = await Promise.all([getKpiLinks(), getKpiEmployees()]);
            setData(l);
            setEmployees(Array.isArray(e) ? e : []);
        } catch (err) {
            showAlert?.(err?.message || "Не удалось загрузить", "error");
        }
    }, [showAlert]);
    useEffect(() => { load(); }, [load]);

    const units = useMemo(() => flatUnits(data?.units || []), [data]);
    const empById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);

    const saveUnits = async (g, ids) => {
        try {
            await setKpiGroupUnits(g.id, ids);
            showAlert?.("Привязка сохранена", "success");
            load();
            return true;
        } catch (err) {
            showAlert?.(err?.message || "Не удалось сохранить", "error");
            return false;
        }
    };
    const saveEmployee = async (card, employeeId) => {
        try {
            const res = await setKpiCardEmployee(card.id, employeeId, true);
            showAlert?.(`Сохранено (карт: ${res?.updated ?? 1})`, "success");
            load();
        } catch (err) {
            showAlert?.(err?.message || "Не удалось сохранить", "error");
        }
    };
    const autoMatch = async (g) => {
        const todo = g.cards.filter((c) => !c.employee_id).map((c) => [c, matchEmployee(c.employee_name || c.title, employees)]).filter(([, e]) => e);
        if (!todo.length) { showAlert?.("Однозначных совпадений по ФИО нет — выберите вручную", "info"); return; }
        if (!window.confirm(`Привязать по ФИО: ${todo.map(([c, e]) => `${c.employee_name || c.title} → ${e.full_name}`).join("; ")}?`)) return;
        try {
            for (const [c, e] of todo) await setKpiCardEmployee(c.id, e.id, true);
            showAlert?.(`Привязано карт: ${todo.length}`, "success");
            load();
        } catch (err) {
            showAlert?.(err?.message || "Не удалось сохранить", "error");
        }
    };

    if (!data) return <p className="text-sm text-slate-500">Загрузка…</p>;
    return (
        <div className="space-y-3">
            <p className="text-sm text-slate-500">
                Таблица КПЭ привязывается к одному или нескольким подразделениям «Структуры компании» — тогда её видно в карточке
                подразделения на странице <Link className="underline" to="/company">«Компания»</Link> и работают правила доступа «руководители / сотрудники подразделений».
                Карты привязываются к сотрудникам — для карточки сотрудника и будущих расчётов ЗП.
            </p>
            {!units.length && <p className="text-sm text-amber-700">Структура пустая — сначала заполните «Структуру компании».</p>}
            {data.groups.map((g) => {
                const linked = g.cards.filter((c) => c.employee_id).length;
                const isOpen = openGroup === g.id;
                return (
                    <section key={g.id} className="rounded-xl border border-slate-200 bg-white">
                        <div className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(180px,1fr)_2fr_auto] md:items-start">
                            <div>
                                <p className="font-semibold text-slate-900">{g.name}{g.archived && <span className="ml-2 text-xs text-slate-400">архив</span>}</p>
                                {g.department && <p className="text-xs text-slate-500">{g.department}</p>}
                            </div>
                            <UnitPicker units={units} value={g.unit_ids || []} onSave={(ids) => saveUnits(g, ids)} />
                            <button type="button" className="text-left text-sm text-slate-600 hover:text-slate-900" onClick={() => setOpenGroup(isOpen ? null : g.id)}>
                                {isOpen ? "▾" : "▸"} карты: {linked}/{g.cards.length} с сотрудником
                            </button>
                        </div>
                        {isOpen && (
                            <div className="border-t border-slate-100 px-4 py-3">
                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                    <p className="text-xs text-slate-500">Карты за {g.last_period ? periodLabel(g.last_period).toLowerCase() : "—"}; привязка ставится и в других месяцах этой таблицы с тем же ФИО.</p>
                                    <div className="flex-1" />
                                    <button type="button" className={btnGhost} onClick={() => autoMatch(g)} disabled={!g.cards.some((c) => !c.employee_id)}>Подобрать по ФИО</button>
                                </div>
                                <table className="min-w-full text-sm">
                                    <tbody>
                                        {g.cards.map((c) => {
                                            const hint = !c.employee_id ? matchEmployee(c.employee_name || c.title, employees) : null;
                                            return (
                                                <tr key={c.id} className="border-t border-slate-100">
                                                    <td className="py-1.5 pr-3">
                                                        <p className="font-medium text-slate-800">{c.employee_name || c.title}</p>
                                                        {c.position && <p className="text-xs text-slate-500">{c.position}</p>}
                                                    </td>
                                                    <td className="py-1.5">
                                                        <select className={`${input} w-full max-w-sm`} value={c.employee_id || ""}
                                                            onChange={(e) => saveEmployee(c, e.target.value ? Number(e.target.value) : null)}>
                                                            <option value="">— не привязана —</option>
                                                            {c.employee_id && !empById.has(c.employee_id) && <option value={c.employee_id}>#{c.employee_id} (уволен или архив)</option>}
                                                            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}{e.position ? ` — ${e.position}` : ""}</option>)}
                                                        </select>
                                                        {hint && <p className="mt-0.5 text-xs text-slate-500">похоже: {hint.full_name} <button type="button" className="underline" onClick={() => saveEmployee(c, hint.id)}>привязать</button></p>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {!g.cards.length && <tr><td className="py-2 text-slate-500">Карт нет</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
}

function AccessTab() {
    const { showAlert } = useAlertContext();
    const [data, setData] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [draft, setDraft] = useState({ subject_type: "erp_role", subject: "", group_id: "", level: "view" });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [a, e] = await Promise.all([getKpiAccess(), getKpiEmployees()]);
            setData(a);
            setEmployees(Array.isArray(e) ? e : []);
        } catch (err) {
            showAlert?.(err?.message || "Не удалось загрузить", "error");
        }
    }, [showAlert]);
    useEffect(() => { load(); }, [load]);

    const groupName = (id) => (id == null ? "Все таблицы" : data?.groups?.find((g) => g.id === id)?.name || `#${id}`);
    const empName = (id) => employees.find((e) => String(e.id) === String(id))?.full_name || `сотрудник #${id}`;
    const who = (r) => {
        const t = data?.subject_types?.[r.subject_type] || r.subject_type;
        if (r.subject_type === "employee") return `${t}: ${empName(r.subject)}`;
        return r.subject ? `${t}: ${r.subject}` : t;
    };
    const needsSubject = !["unit_heads", "unit_members"].includes(draft.subject_type);

    const add = async () => {
        setBusy(true);
        try {
            await addKpiAccessRule({ ...draft, group_id: draft.group_id || null, subject: needsSubject ? draft.subject : null });
            setDraft((d) => ({ ...d, subject: "" }));
            showAlert?.("Правило добавлено", "success");
            load();
        } catch (err) {
            showAlert?.(err?.message || "Не удалось добавить", "error");
        } finally {
            setBusy(false);
        }
    };
    const remove = async (r) => {
        if (!window.confirm(`Удалить правило «${who(r)} — ${groupName(r.group_id)}»?`)) return;
        try {
            await deleteKpiAccessRule(r.id);
            load();
        } catch (err) {
            showAlert?.(err?.message || "Не удалось удалить", "error");
        }
    };

    if (!data) return <p className="text-sm text-slate-500">Загрузка…</p>;
    return (
        <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                <p>Действует самое широкое из подходящих правил. <b>Смотрит</b> — сводки и карты; <b>Правит карты</b> — карты, месяцы, грейды;
                    <b> Управляет</b> — ещё таблицы, перенос, привязки и этот экран.</p>
                <p className="mt-1">Всегда: {data.always}. Права из «Доступов»: {Object.entries(data.grants || {}).map(([k, v]) => `${k} — ${v}`).join("; ")}.</p>
                <p className="mt-1">«Руководители / сотрудники подразделений» срабатывают для таблиц, привязанных к подразделениям (вкладка «Привязка»); руководитель видит и дочерние.</p>
            </div>

            <div className="flex flex-wrap items-end gap-2 rounded-xl border border-dashed border-slate-300 bg-white p-3">
                <label className="text-xs text-slate-500">Кому
                    <select className={`${input} mt-1 block`} value={draft.subject_type} onChange={(e) => setDraft({ ...draft, subject_type: e.target.value, subject: "" })}>
                        {Object.entries(data.subject_types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </label>
                {needsSubject && (draft.subject_type === "employee" ? (
                    <label className="text-xs text-slate-500">Сотрудник
                        <select className={`${input} mt-1 block min-w-64`} value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}>
                            <option value="">— выберите —</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                        </select>
                    </label>
                ) : (
                    <label className="text-xs text-slate-500">{draft.subject_type === "erp_role" ? "Роль ERP (например hr, lead, manager)" : "Роль платформы (из «Доступов»)"}
                        <input className={`${input} mt-1 block min-w-56`} value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
                    </label>
                ))}
                <label className="text-xs text-slate-500">К чему
                    <select className={`${input} mt-1 block min-w-48`} value={draft.group_id} onChange={(e) => setDraft({ ...draft, group_id: e.target.value })}>
                        <option value="">Все таблицы КПЭ</option>
                        {(data.groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                </label>
                <label className="text-xs text-slate-500">Уровень
                    <select className={`${input} mt-1 block`} value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value })}>
                        {Object.entries(data.levels || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </label>
                <button type="button" className={btnMain} onClick={add} disabled={busy || (needsSubject && !String(draft.subject).trim())}>Добавить правило</button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-full text-sm">
                    <thead className="text-xs text-slate-500">
                        <tr className="text-left">
                            <th className="px-4 py-2 font-medium">Кому</th>
                            <th className="px-2 py-2 font-medium">К чему</th>
                            <th className="px-2 py-2 font-medium">Уровень</th>
                            <th className="px-2 py-2 font-medium">Добавил</th>
                            <th className="px-4 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {data.rules.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100">
                                <td className="px-4 py-2">{who(r)}</td>
                                <td className="px-2 py-2">{groupName(r.group_id)}</td>
                                <td className="px-2 py-2">{data.levels?.[r.level] || r.level}</td>
                                <td className="px-2 py-2 text-slate-500">{r.created_by || "—"}</td>
                                <td className="px-4 py-2 text-right">
                                    <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => remove(r)}>удалить</button>
                                </td>
                            </tr>
                        ))}
                        {!data.rules.length && <tr><td colSpan={5} className="px-4 py-3 text-slate-500">Правил нет — KPI видят только владелец платформы и обладатели прав из «Доступов».</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function KpiSettingsPage() {
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") === "access" ? "access" : "links";
    return (
        <MainLayout>
            <div className="space-y-4">
                <Link to="/kpi" className="text-sm text-slate-500 hover:text-slate-800">← KPI</Link>
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-xl font-bold text-slate-900">Настройки KPI</h1>
                    <div className="flex overflow-hidden rounded-lg border border-slate-200 text-sm">
                        {[["links", "Привязка"], ["access", "Доступ"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setParams(k === "links" ? {} : { tab: k })}
                                className={`px-3 py-1.5 ${tab === k ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>{l}</button>
                        ))}
                    </div>
                </div>
                {tab === "links" ? <LinksTab /> : <AccessTab />}
            </div>
        </MainLayout>
    );
}
